/**
 * FindingInlineEditor -- Opens FindingFormDialog in edit mode for an existing finding
 *
 * Caller controls open state via isEditing + onSave/onCancel props.
 */

import { useUpdateFinding } from "@/packages/features-shared";
import type { Id } from "@/packages/types";
import type { SeverityType } from "@/packages/domains/findings";
import { FindingFormDialog } from "./FindingFormDialog";
import type { FindingFormValues } from "./FindingFormDialog";

interface EditableFinding {
  _id: Id<"findings">;
  title: string;
  observation: string;
  recommendation: string;
  severity: SeverityType;
}

interface FindingInlineEditorProps {
  finding: EditableFinding;
  isEditing: boolean;
  onSave: () => void;
  onCancel: () => void;
}

export function FindingInlineEditor({
  finding,
  isEditing,
  onSave,
  onCancel,
}: FindingInlineEditorProps) {
  const updateMutation = useUpdateFinding();

  async function handleSubmit(values: FindingFormValues) {
    await updateMutation.mutateAsync({
      id: finding._id,
      title: values.title || undefined,
      severity: values.severity,
      observation: values.observation || undefined,
      recommendation: values.recommendation || undefined,
    });
    onSave();
  }

  return (
    <FindingFormDialog
      open={isEditing}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      mode="edit"
      initialValues={{
        title: finding.title,
        severity: finding.severity,
        observation: finding.observation,
        recommendation: finding.recommendation,
      }}
      isPending={updateMutation.isPending}
      onSubmit={handleSubmit}
    />
  );
}
